"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Affinity = void 0;
const vc_common_1 = require("@affinidi/vc-common");
const vc_common_2 = require("@affinidi/vc-common");
const did_resolver_1 = require("did-resolver");
const tiny_lds_ecdsa_secp256k1_2019_1 = require("@affinidi/tiny-lds-ecdsa-secp256k1-2019");
const affinity_metrics_lib_1 = require("@affinidi/affinity-metrics-lib");
const _defaultConfig_1 = require("./_defaultConfig");
const services_1 = require("./services");
const _baseDocumentLoader_1 = require("./_baseDocumentLoader");
const revocationList = require('vc-revocation-list'); // eslint-disable-line
let fetch;
if (!fetch) {
    fetch = require('node-fetch');
}
class Affinity {
    constructor(options = {}) {
        this._documentLoader = (url) => __awaiter(this, void 0, void 0, function* () {
            if (url.startsWith('did:')) {
                const did = url.indexOf('#') >= 0 ? services_1.DidDocumentService.keyIdToDid(url) : url;
                const didDocument = yield this.resolveDid(did);
                return {
                    contextUrl: null,
                    document: didDocument,
                    documentUrl: url,
                };
            }
            return _baseDocumentLoader_1.baseDocumentLoader(url);
        });
        this._apiKey = options.apiKey;
        this._registryUrl = options.registryUrl || _defaultConfig_1.DEFAULT_REGISTRY_URL;
        this._metricsUrl = options.metricsUrl || _defaultConfig_1.DEFAULT_METRICS_URL;
        this._component = options.component || affinity_metrics_lib_1.EventComponent.AffinidiCommon;
        this._digestService = new services_1.DigestService();
        this._metricsService = new services_1.MetricsService({
            metricsUrl: this._metricsUrl,
            accessApiKey: this._apiKey,
            component: this._component,
        });
    }
    _resolveDidIfNoDidDocument(did, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            const isDidResolveNotRequired = !!didDocument && did_resolver_1.parse(did).did === did_resolver_1.parse(didDocument.id).did;
            if (isDidResolveNotRequired) {
                return didDocument;
            }
            return this.resolveDid(did);
        });
    }
    resolveDid(did) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${this._registryUrl}/api/v1/did/resolve-did`;
            const body = JSON.stringify({ did }, null, 2);
            const headers = { Accept: 'application/json', 'Api-Key': this._apiKey, 'Content-Type': 'application/json' };
            const response = yield fetch(url, { method: 'POST', headers, body });
            const result = yield response.json();
            if (response.status.toString().startsWith('2')) {
                return result.didDocument;
            }
            else {
                let error = new Error(result);
                if (result.message) {
                    error = new Error(result.message);
                }
                throw error;
            }
        });
    }
    validateJWT(encryptedtoken, initialEncryptedtoken, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = Affinity.fromJwt(encryptedtoken);
            const { payload } = token;
            const did = services_1.DidDocumentService.keyIdToDid(payload.iss);
            didDocument = yield this._resolveDidIfNoDidDocument(did, didDocument);
            const publicKey = services_1.DidDocumentService.getPublicKey(payload.iss, didDocument, payload.kid);
            const { digest: tokenDigest, signature } = this._digestService.getTokenDigest(token);
            const isSignatureVerified = services_1.KeysService.verify(tokenDigest, publicKey, signature);
            const eventOptions = { link: did, name: affinity_metrics_lib_1.EventName.VP_VERIFIED_JWT };
            if (!isSignatureVerified) {
                if (payload.typ === 'credentialResponse') {
                    // send failed VP_VERIFIED_JWT event due to invalid signature
                    eventOptions.verificationMetadata = {
                        isValid: false,
                        invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.JWT_INVALID_SIGNATURE,
                    };
                    this._metricsService.sendVpEvent(eventOptions);
                }
                throw new Error('Signature on token is invalid');
            }
            if (payload.exp < Date.now()) {
                if (payload.typ === 'credentialResponse') {
                    // send failed VP_VERIFIED_JWT event due to token expired
                    eventOptions.verificationMetadata = {
                        isValid: false,
                        invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.JWT_EXPIRED,
                    };
                    this._metricsService.sendVpEvent(eventOptions);
                }
                throw new Error('Token expired');
            }
            if (initialEncryptedtoken) {
                const sendToken = Affinity.fromJwt(initialEncryptedtoken);
                if (sendToken.payload.jti !== payload.jti) {
                    if (payload.typ === 'credentialResponse') {
                        // send failed VP_VERIFIED_JWT event due to token nonce mismatch
                        eventOptions.verificationMetadata = {
                            isValid: false,
                            invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.JWT_NONCE_MISMATCHED,
                        };
                        this._metricsService.sendVpEvent(eventOptions);
                    }
                    throw new Error('The token nonce does not match the request');
                }
                if (payload.aud && sendToken.payload.iss) {
                    const responseAudienceDid = did_resolver_1.parse(payload.aud).did; // the one who received and verified the VP
                    const requestIssuerDid = did_resolver_1.parse(sendToken.payload.iss).did; // the one who requested the VP to verify
                    if (requestIssuerDid !== responseAudienceDid) {
                        if (payload.typ === 'credentialResponse') {
                            // send failed VP_VERIFIED_JWT event due to audience mismatch:
                            // the verifier who verified the VP is not the one who requested for it
                            eventOptions.verificationMetadata = {
                                isValid: false,
                                invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.JWT_VERIFIER_MISMATCHED,
                            };
                            this._metricsService.sendVpEvent(eventOptions);
                        }
                        throw new Error('The request token issuer does not match audience of the response token');
                    }
                }
                if (sendToken.payload.aud) {
                    const requestAudienceDid = did_resolver_1.parse(sendToken.payload.aud).did; // the intended holder requested by the verifier
                    const responseIssuerDid = did_resolver_1.parse(did).did; // the holder who sent the VP in response to the verifier
                    if (requestAudienceDid !== responseIssuerDid) {
                        if (payload.typ === 'credentialResponse') {
                            // send failed VP_VERIFIED_JWT event due to audience mismatch:
                            // the holder who sent the VP is not the one specified by verifier in the original request
                            eventOptions.verificationMetadata = {
                                isValid: false,
                                invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.JWT_HOLDER_MISMATCHED,
                            };
                            this._metricsService.sendVpEvent(eventOptions);
                        }
                        throw new Error('The token issuer does not match audience of the request token');
                    }
                }
            }
            // send successful VP_VERIFIED_JWT event
            if (payload.typ === 'credentialResponse') {
                eventOptions.verificationMetadata = { isValid: true };
                this._metricsService.sendVpEvent(eventOptions);
            }
        });
    }
    _validateLegacyCredential(credential, holderKey, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            let result;
            const now = new Date();
            const expires = new Date(credential.expires);
            const isExpired = expires.getTime() < now.getTime();
            if (isExpired) {
                return { result: false, error: `${credential.id}: VC is expired.` };
            }
            if (holderKey) {
                const holderDid = services_1.DidDocumentService.keyIdToDid(holderKey);
                let holderIsSubject;
                if (credential.claim) {
                    holderIsSubject = holderDid === services_1.DidDocumentService.keyIdToDid(credential.claim.id); // to support already created creds
                }
                else {
                    holderIsSubject = holderDid === services_1.DidDocumentService.keyIdToDid(credential.credentialSubject.id);
                }
                if (!holderIsSubject) {
                    return { result: false, error: `${credential.id}: Holder is not the subject of VC.` };
                }
            }
            try {
                const issuerPublicKey = yield this._getCredentialIssuerPublicKey(credential, didDocument);
                const { digest, signature } = yield this._digestService.getJsonLdDigest(credential);
                result = services_1.KeysService.verify(digest, issuerPublicKey, signature);
                if (result !== true) {
                    return { result, error: `${credential.id}: Signature is not valid.` };
                }
            }
            catch (error) {
                return { result: false, error: `${credential.id}: ${error.message}` };
            }
            return { result, error: '' };
        });
    }
    _checkCredentialStatus(credential) {
        return __awaiter(this, void 0, void 0, function* () {
            if (credential.credentialStatus) {
                // We don't need to have `revocationList.checkStatus` verify the VC because we already do that
                const verifyRevocationListCredential = false;
                const documentLoader = this._documentLoader;
                const { verified, error } = yield revocationList.checkStatus({
                    credential,
                    documentLoader,
                    verifyRevocationListCredential,
                });
                return { verified, error };
            }
            return { verified: true };
        });
    }
    validateCredential(credential, holderKey, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            if (credential.claim) {
                return this._validateLegacyCredential(credential, holderKey, didDocument);
            }
            else {
                const result = yield vc_common_2.validateVCV1({
                    documentLoader: this._documentLoader,
                    getVerifySuite: ({ proofType, verificationMethod, controller }) => __awaiter(this, void 0, void 0, function* () {
                        if (proofType !== 'EcdsaSecp256k1Signature2019') {
                            throw new Error(`Unsupported proofType: ${proofType}`);
                        }
                        const resolvedDidDocument = yield this._resolveDidIfNoDidDocument(controller, didDocument);
                        const publicKeyHex = services_1.DidDocumentService.getPublicKey(verificationMethod, resolvedDidDocument).toString('hex');
                        return new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Signature({
                            key: new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Key({
                                publicKeyHex: publicKeyHex,
                                id: verificationMethod,
                                controller,
                            }),
                        });
                    }),
                    getProofPurposeOptions: ({ proofPurpose, controller }) => __awaiter(this, void 0, void 0, function* () {
                        if (proofPurpose === 'assertionMethod') {
                            const resolvedDidDoc = yield this._resolveDidIfNoDidDocument(controller, didDocument);
                            return {
                                controller: resolvedDidDoc,
                            };
                        }
                        throw new Error(`Unsupported proofPurpose: ${proofPurpose}`);
                    }),
                })(credential);
                let eventOptions;
                if (result.kind === 'invalid') {
                    let legacyValidated;
                    try {
                        // Cover the case where a credential was signed the old way but used "credentialSubject" instead of "claim"
                        legacyValidated = yield this._validateLegacyCredential(credential, holderKey, didDocument);
                    }
                    catch (_a) {
                        // We don't need to handle caught errors
                    }
                    if (legacyValidated && legacyValidated.result) {
                        return legacyValidated;
                    }
                    else {
                        const errors = result.errors.map((error) => `${error.kind}: ${error.message}`).join('\n');
                        // send failed VC_VERIFIED event due to generic error
                        if (credential.holder) {
                            // TODO: also send the event when holder is not available, maybe add a metadata property to indicate that
                            const eventOptions = {
                                link: credential.holder.id,
                                name: affinity_metrics_lib_1.EventName.VC_VERIFIED,
                            };
                            eventOptions.verificationMetadata = {
                                isValid: false,
                                invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.ERROR,
                                errorMessage: errors,
                            };
                            this._metricsService.sendVcEvent(credential, eventOptions);
                        }
                        return {
                            result: false,
                            error: `${credential.id}: The following errors have occurred:\n${errors}`,
                        };
                    }
                }
                else if (holderKey) {
                    const holderDid = services_1.DidDocumentService.keyIdToDid(holderKey);
                    if (did_resolver_1.parse(result.data.holder.id).did !== did_resolver_1.parse(holderDid).did) {
                        // send failed VC_VERIFIED event due to holder mismatch
                        eventOptions = {
                            link: did_resolver_1.parse(result.data.holder.id).did,
                            name: affinity_metrics_lib_1.EventName.VC_VERIFIED,
                            verificationMetadata: {
                                isValid: false,
                                invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.HOLDER_MISMATCHED,
                            },
                        };
                        this._metricsService.sendVcEvent(credential, eventOptions);
                        return { result: false, error: `${credential.id}: The provided holder is not holder of this credential.` };
                    }
                }
                // check revocation status
                const { verified, error } = yield this._checkCredentialStatus(credential);
                if (!verified) {
                    // send failed VC_VERIFIED event due to revocation
                    eventOptions = {
                        link: did_resolver_1.parse(result.data.holder.id).did,
                        name: affinity_metrics_lib_1.EventName.VC_VERIFIED,
                        verificationMetadata: { isValid: false, invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.REVOKED },
                    };
                    this._metricsService.sendVcEvent(credential, eventOptions);
                    return { result: false, error };
                }
                // send VC_VERIFIED event when verification is successful
                if (result.data.holder) {
                    // TODO: also send the event when holder is not available, maybe add a metadata property to indicate that
                    const eventOptions = {
                        link: did_resolver_1.parse(result.data.holder.id).did,
                        name: affinity_metrics_lib_1.EventName.VC_VERIFIED,
                    };
                    this._metricsService.sendVcEvent(credential, eventOptions);
                }
                return { result: true, error: '' };
            }
        });
    }
    signCredential(unsignedCredential, encryptedSeed, encryptionKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const keyService = new services_1.KeysService(encryptedSeed, encryptionKey);
            const { seed, didMethod } = keyService.decryptSeed();
            const didDocumentService = new services_1.DidDocumentService(keyService);
            const did = didDocumentService.getMyDid();
            const signedVc = vc_common_1.buildVCV1({
                unsigned: unsignedCredential,
                issuer: {
                    did,
                    keyId: didDocumentService.getKeyId(),
                    privateKey: services_1.KeysService.getPrivateKey(seed.toString('hex'), didMethod).toString('hex'),
                },
                getSignSuite: ({ keyId, privateKey, controller }) => {
                    return new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Signature({
                        key: new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Key({
                            id: keyId,
                            controller,
                            privateKeyHex: privateKey,
                        }),
                    });
                },
                documentLoader: this._documentLoader,
                getProofPurposeOptions: () => __awaiter(this, void 0, void 0, function* () {
                    return ({
                        controller: yield didDocumentService.buildDidDocument(),
                    });
                }),
            });
            // send VC_SIGNED event
            if (unsignedCredential.holder) {
                // TODO: also send the event when holder is not available, maybe add a metadata property to indicate that
                const eventOptions = {
                    link: did_resolver_1.parse(unsignedCredential.holder.id).did,
                    secondaryLink: unsignedCredential.id,
                    name: affinity_metrics_lib_1.EventName.VC_SIGNED,
                };
                this._metricsService.sendVcEvent(unsignedCredential, eventOptions);
            }
            return signedVc;
        });
    }
    validatePresentation(vp, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield vc_common_2.validateVPV1({
                documentLoader: this._documentLoader,
                getVerifySuite: ({ proofType, verificationMethod, controller }) => __awaiter(this, void 0, void 0, function* () {
                    if (proofType !== 'EcdsaSecp256k1Signature2019') {
                        throw new Error(`Unsupported proofType: ${proofType}`);
                    }
                    const resolvedDidDocument = yield this._resolveDidIfNoDidDocument(controller, didDocument);
                    const publicKeyHex = services_1.DidDocumentService.getPublicKey(verificationMethod, resolvedDidDocument).toString('hex');
                    return new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Signature({
                        key: new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Key({
                            publicKeyHex: publicKeyHex,
                            id: verificationMethod,
                            controller,
                        }),
                    });
                }),
                getProofPurposeOptions: ({ proofPurpose, controller }) => __awaiter(this, void 0, void 0, function* () {
                    switch (proofPurpose) {
                        case 'authentication':
                        case 'assertionMethod': {
                            const resolvedDidDoc = yield this._resolveDidIfNoDidDocument(controller, didDocument);
                            return {
                                controller: resolvedDidDoc,
                            };
                        }
                        default:
                            throw new Error(`Unsupported proofPurpose: ${proofPurpose}`);
                    }
                }),
            })(vp);
            let eventOptions;
            if (result.kind === 'invalid') {
                const errors = result.errors.map((error) => `${error.kind}: ${error.message}`).join('\n');
                // send failed VP_VERIFIED event due to generic error
                eventOptions = {
                    link: vp.holder.id,
                    name: affinity_metrics_lib_1.EventName.VP_VERIFIED,
                    verificationMetadata: {
                        isValid: false,
                        invalidReason: affinity_metrics_lib_1.VerificationInvalidReason.ERROR,
                        errorMessage: errors,
                    },
                };
                this._metricsService.sendVpEvent(eventOptions);
                return {
                    result: false,
                    error: `${vp.id}: The following errors have occurred:\n${errors}`,
                };
            }
            // send successful VP_VERIFIED event
            eventOptions = {
                link: did_resolver_1.parse(result.data.holder.id).did,
                name: affinity_metrics_lib_1.EventName.VP_VERIFIED,
                verificationMetadata: { isValid: true },
            };
            this._metricsService.sendVpEvent(eventOptions);
            return { result: true, data: result.data };
        });
    }
    signPresentation(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const keyService = new services_1.KeysService(opts.encryption.seed, opts.encryption.key);
            const { seed, didMethod } = keyService.decryptSeed();
            const didDocumentService = new services_1.DidDocumentService(keyService);
            const did = didDocumentService.getMyDid();
            const signedVp = vc_common_1.buildVPV1({
                unsigned: opts.vp,
                holder: {
                    did,
                    keyId: didDocumentService.getKeyId(),
                    privateKey: services_1.KeysService.getPrivateKey(seed.toString('hex'), didMethod).toString('hex'),
                },
                getSignSuite: ({ keyId, privateKey, controller }) => {
                    return new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Signature({
                        key: new tiny_lds_ecdsa_secp256k1_2019_1.Secp256k1Key({
                            id: keyId,
                            controller,
                            privateKeyHex: privateKey,
                        }),
                    });
                },
                documentLoader: this._documentLoader,
                getProofPurposeOptions: () => ({
                    challenge: opts.purpose.challenge,
                    domain: opts.purpose.domain,
                }),
            });
            // send VP_SIGNED event
            const eventOptions = {
                link: did,
                secondaryLink: opts.vp.id,
                name: affinity_metrics_lib_1.EventName.VP_SIGNED,
            };
            this._metricsService.sendVpEvent(eventOptions);
            return signedVp;
        });
    }
    _getCredentialIssuerPublicKey(credential, didDocument) {
        return __awaiter(this, void 0, void 0, function* () {
            const keyId = credential.proof.creator || credential.proof.verificationMethod;
            const did = services_1.DidDocumentService.keyIdToDid(keyId);
            didDocument = yield this._resolveDidIfNoDidDocument(did, didDocument);
            return services_1.DidDocumentService.getPublicKey(keyId, didDocument);
        });
    }
    static fromJwt(jwt) {
        return services_1.JwtService.fromJWT(jwt);
    }
    signJWTObject(jwtObject, encryptedSeed, encryptionKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const signedJwtObject = Affinity.signJWTObject(jwtObject, encryptedSeed, encryptionKey);
            // send VP_SIGNED_JWT event
            if (jwtObject.payload.typ === 'credentialResponse') {
                const eventOptions = {
                    link: jwtObject.payload.iss,
                    secondaryLink: jwtObject.payload.jti,
                    name: affinity_metrics_lib_1.EventName.VP_SIGNED_JWT,
                };
                this._metricsService.sendVpEvent(eventOptions);
            }
            return signedJwtObject;
        });
    }
    // to be deprecated and replaced with instance method
    static signJWTObject(jwtObject, encryptedSeed, encryptionKey) {
        const keyService = new services_1.KeysService(encryptedSeed, encryptionKey);
        return keyService.signJWT(jwtObject);
    }
    static encodeObjectToJWT(jwtObject) {
        const jwtService = new services_1.JwtService();
        return jwtService.encodeObjectToJWT(jwtObject);
    }
    static encryptSeed(seedHexWithMethod, encryptionKey) {
        const encryptionKeyBuffer = services_1.KeysService.normalizePassword(encryptionKey);
        return services_1.KeysService.encryptSeed(seedHexWithMethod, encryptionKeyBuffer);
    }
}
exports.Affinity = Affinity;
//# sourceMappingURL=Affinity.js.map